export interface Tag {
  id: number;
  name: string;
  color: string;
  created_at: string;
}

export interface Image {
  id: number;
  filename: string;
  original_filename: string;
  title: string;
  description: string;
  category: string;
  file_size: number;
  file_size_mb: number;
  width?: number;
  height?: number;
  created_at: string;
  updated_at: string;
  url: string;
  tags: Tag[];
  primary_tag?: Tag;
  primary_tag_id?: number;
  // Physical properties
  length?: number;
  max_thickness?: number;
  fully_insertable?: boolean;
  softness?: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

export interface AccessToken {
  id: number;
  token: string;
  name: string;
  permissions: string;
  created_at: string;
  expires_at: string;
  time_remaining: string;
  is_expired: boolean;
  creator_username: string;
  last_used?: string;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    // Use the domain name with HTTPS to avoid CORS/mixed content issues
    // nginx will proxy to the backend
    this.baseUrl = 'https://pucollection.com/api';

    // Debug log to see what URL is being used
    console.log('API Service initialized with baseUrl:', this.baseUrl);
    console.log('Current window location:', window.location.href);
  }
  private accessToken: string | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    // First check the in-memory token
    if (this.accessToken) {
      return this.accessToken;
    }

    // Fall back to localStorage for guest users
    return localStorage.getItem('access_token');
  }

  private async request(endpoint: string, options: RequestInit & { timeoutMs?: number } = {}): Promise<any> {
    const url = `${this.baseUrl}/${endpoint}`;
    console.log('🌐 Making API request to:', url);
    console.log('📤 Request options:', { method: options.method, body: options.body });

    const { timeoutMs = 5000, ...restOptions } = options;

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const defaultHeaders: Record<string, string> = {};

    // Only set Content-Type for non-FormData requests
    if (!(restOptions.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    // Add authorization header if we have an access token
    if (this.accessToken) {
      defaultHeaders['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const config: RequestInit = {
      ...restOptions,
      credentials: 'include', // Include cookies for session management
      signal: controller.signal, // Add abort signal
      headers: {
        ...defaultHeaders,
        ...restOptions.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId); // Clear timeout on successful response

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;

        try {
          const errorPayload = await response.json();
          if (errorPayload?.error) {
            errorMessage = errorPayload.error;
          }
        } catch (parseError) {
          console.warn('Unable to parse API error response:', parseError);
        }

        if (response.status === 401) {
          // Unauthorized - clear token and redirect to login
          this.accessToken = null;
          localStorage.removeItem('access_token');
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Request timeout - server took too long to respond');
      }
      throw error;
    }
  }

  async getImages(page: number = 1, perPage: number = 12, tagId?: number, primaryTagId?: number, fullyInsertable?: boolean): Promise<any> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString()
    });

    if (primaryTagId) {
      params.append('primary_tag_id', primaryTagId.toString());
    } else if (tagId) {
      params.append('tag_id', tagId.toString());
    }

    if (fullyInsertable !== undefined) {
      params.append('fully_insertable', fullyInsertable.toString());
    }

    return this.request(`images?${params.toString()}`);
  }

  async uploadImage(
    file: File,
    title: string,
    description: string,
    category?: string,
    length?: string,
    max_thickness?: string,
    fully_insertable?: boolean,
    softness?: string,
    primary_tag_id?: string
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('category', category || 'general');

    if (length) formData.append('length', length);
    if (max_thickness) formData.append('max_thickness', max_thickness);
    if (fully_insertable !== undefined) formData.append('fully_insertable', fully_insertable.toString());
    if (softness) formData.append('softness', softness);
    if (primary_tag_id) formData.append('primary_tag_id', primary_tag_id);

    return this.request('images', {
      method: 'POST',
      body: formData,
      headers: {},
      timeoutMs: 60000, // 60s for file uploads
    });
  }

  async updateImage(id: number, data: {
    title?: string;
    description?: string;
    category?: string;
    length?: string;
    max_thickness?: string;
    fully_insertable?: boolean;
    softness?: string;
  }): Promise<any> {
    return this.request(`images/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteImage(id: number): Promise<any> {
    return this.request(`images/${id}`, {
      method: 'DELETE'
    });
  }

  async getStats(): Promise<any> {
    return this.request('stats');
  }

  // Tag management methods
  async getTags(): Promise<any> {
    return this.request('tags');
  }

  async getPrimaryTags(): Promise<any> {
    return this.request('tags');  // Same endpoint since primary tags are just regular tags
  }

  async createTag(name: string, color: string): Promise<any> {
    return this.request('tags', {
      method: 'POST',
      body: JSON.stringify({ name, color })
    });
  }

  async updateTag(id: number, data: { name?: string; color?: string }): Promise<any> {
    return this.request(`tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteTag(id: number): Promise<any> {
    return this.request(`tags/${id}`, {
      method: 'DELETE'
    });
  }

  // Image-tag association methods
  async addTagToImage(imageId: number, tagId: number): Promise<any> {
    return this.request(`images/${imageId}/tags`, {
      method: 'POST',
      body: JSON.stringify({ tag_id: tagId })
    });
  }

  async removeTagFromImage(imageId: number, tagId: number): Promise<any> {
    return this.request(`images/${imageId}/tags/${tagId}`, {
      method: 'DELETE'
    });
  }

  async setPrimaryTag(imageId: number, tagId?: number): Promise<any> {
    return this.request(`images/${imageId}/primary-tag`, {
      method: 'PUT',
      body: JSON.stringify({ tag_id: tagId })
    });
  }

  // Authentication methods
  async login(username: string, password: string): Promise<any> {
    console.log('🔐 Attempting login with username:', username);
    console.log('🌐 Using baseUrl:', this.baseUrl);

    try {
      const result = await this.request('auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password })
      });
      console.log('✅ Login API response:', result);
      return result;
    } catch (error) {
      console.error('❌ Login API error:', error);
      throw error;
    }
  }

  async logout(): Promise<any> {
    const result = await this.request('auth/logout', {
      method: 'POST'
    });
    this.accessToken = null;
    localStorage.removeItem('access_token');
    return result;
  }

  async getCurrentUser(): Promise<any> {
    return this.request('auth/me');
  }

  async createAdmin(username: string, password: string, email: string): Promise<any> {
    return this.request('auth/create-admin', {
      method: 'POST',
      body: JSON.stringify({ username, password, email })
    });
  }

  // Access token management methods
  async getAccessTokens(): Promise<any> {
    return this.request('access-tokens');
  }

  async createAccessToken(hours: number = 1, name: string = 'Guest Access', permissions: string = 'read'): Promise<any> {
    return this.request('access-tokens', {
      method: 'POST',
      body: JSON.stringify({ hours, name, permissions })
    });
  }

  async revokeAccessToken(tokenId: number): Promise<any> {
    return this.request(`access-tokens/${tokenId}`, {
      method: 'DELETE'
    });
  }

  async validateToken(token: string): Promise<any> {
    return this.request('validate-token', {
      method: 'POST',
      body: JSON.stringify({ token })
    });
  }

  async getTokenAccessLogs(tokenId: number, page: number = 1, perPage: number = 50): Promise<any> {
    return this.request(`access-tokens/${tokenId}/logs?page=${page}&per_page=${perPage}`);
  }
}

export const apiService = new ApiService();
